import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Bell, Repeat, Lock, Crown, ChevronRight, MapPin, Navigation, Loader2 } from 'lucide-react';
import { format, parse } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useCalendar } from '../../contexts/CalendarContext';
import { UPGRADE_MESSAGES } from '../../utils/membershipConstants';

const EVENT_COLORS = ['#3d8ef0', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const ISTANBUL = { lat: 41.0082, lon: 28.9784, zoom: 11 };

const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  try { return format(parse(dateStr, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy'); }
  catch { return dateStr; }
};

const formatDateFromDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  try {
    const [d, m, y] = dateStr.split('/');
    if (d && m && y) return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    return dateStr;
  } catch { return dateStr; }
};

const isValidDate = (dateStr) => {
  if (!dateStr) return false;
  try {
    const [d, m, y] = dateStr.split('/').map(Number);
    if (!d || !m || !y) return false;
    if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > 2100) return false;
    const date = new Date(y, m - 1, d);
    return date.getDate() === d && date.getMonth() === m - 1 && date.getFullYear() === y;
  } catch { return false; }
};

// Leaflet tek seferlik yükleme
let leafletLoadPromise = null;
const loadLeaflet = () => {
  if (window.L) return Promise.resolve();
  if (leafletLoadPromise) return leafletLoadPromise;

  leafletLoadPromise = new Promise((resolve) => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = resolve;
    script.onerror = resolve;
    document.head.appendChild(script);
  });

  return leafletLoadPromise;
};

const parseNominatimName = (result) => {
  const parts = result.display_name.split(', ');
  return { main: parts[0] || result.display_name, secondary: parts.slice(1).join(', ') };
};

const CreateEventModal = ({ isOpen, onClose, onCreate, onUpdate, selectedDate, calendar, mode = 'create', initialEvent = null }) => {
  const { t, i18n } = useTranslation('calendar');
  const { canUseRecurringEvents } = useCalendar();
  const isEdit = mode === 'edit' && initialEvent;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState(selectedDate ? format(selectedDate, 'dd/MM/yyyy') : format(new Date(), 'dd/MM/yyyy'));
  const [endDate, setEndDate] = useState(selectedDate ? format(selectedDate, 'dd/MM/yyyy') : format(new Date(), 'dd/MM/yyyy'));
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [allDay, setAllDay] = useState(false);
  const [reminderDay, setReminderDay] = useState('0');
  const [reminderHour, setReminderHour] = useState('09:00');
  const [repeatType, setRepeatType] = useState('none');
  const [repeatInterval, setRepeatInterval] = useState(1);
  const [weeklyRepeatCount, setWeeklyRepeatCount] = useState(1);
  const [weeklyRepeatError, setWeeklyRepeatError] = useState('');
  const [monthlyRepeatCount, setMonthlyRepeatCount] = useState(1);
  const [monthlyRepeatError, setMonthlyRepeatError] = useState('');
  const [showUpgradeToast, setShowUpgradeToast] = useState(false);
  const [eventColor, setEventColor] = useState('#3d8ef0');
  const [showReminderExpanded, setShowReminderExpanded] = useState(false);
  const [showRepeatExpanded, setShowRepeatExpanded] = useState(false);

  // Konum
  const [location, setLocation] = useState('');
  const [locationLat, setLocationLat] = useState(null);
  const [locationLon, setLocationLon] = useState(null);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [suggestionPos, setSuggestionPos] = useState({ top: 0, left: 0, width: 0 });

  const mapContainerRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markerRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const locationInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const inputWrapperRef = useRef(null);

  // Haritayı başlat
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled || !mapContainerRef.current || leafletMapRef.current) return;
      await loadLeaflet();
      if (cancelled || !mapContainerRef.current || !window.L || leafletMapRef.current) return;

      const L = window.L;
      const map = L.map(mapContainerRef.current, {
        center: [ISTANBUL.lat, ISTANBUL.lon],
        zoom: ISTANBUL.zoom,
        zoomControl: false,
        attributionControl: false,
      });

      // CartoDB Positron — açık gri, temiz ve minimal
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        subdomains: 'abcd',
        maxZoom: 19,
      }).addTo(map);

      // Zoom kontrolü sağ-alta
      L.control.zoom({ position: 'bottomright' }).addTo(map);

      leafletMapRef.current = map;
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        markerRef.current = null;
      }
    };
  }, [isOpen]);

  // Konum seçilince marker + pan
  useEffect(() => {
    if (!leafletMapRef.current || !window.L) return;
    const L = window.L;

    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }

    if (!locationLat || !locationLon) return;

    const lat = parseFloat(locationLat);
    const lon = parseFloat(locationLon);

    const icon = L.divIcon({
      html: `<div style="width:14px;height:14px;background:#3d8ef0;border:2.5px solid #fff;border-radius:50%;box-shadow:0 0 0 4px rgba(61,142,240,0.25)"></div>`,
      className: '',
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    });

    markerRef.current = L.marker([lat, lon], { icon }).addTo(leafletMapRef.current);
    leafletMapRef.current.flyTo([lat, lon], 15, { duration: 0.8 });
  }, [locationLat, locationLon]);

  // Öneri dropdown pozisyonu — input'un ekran konumuna göre hesaplanır
  useEffect(() => {
    if (!showSuggestions || !inputWrapperRef.current) return;
    const rect = inputWrapperRef.current.getBoundingClientRect();
    setSuggestionPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, [showSuggestions, location]);

  // Dışarı tıklayınca öneriler kapansın
  useEffect(() => {
    const handler = (e) => {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
        locationInputRef.current && !locationInputRef.current.contains(e.target)
      ) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (selectedDate && !isEdit) {
      const s = format(selectedDate, 'dd/MM/yyyy');
      setStartDate(s); setEndDate(s);
    }
  }, [selectedDate, isEdit]);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && initialEvent) {
      setTitle(initialEvent.title || '');
      setDescription(initialEvent.description || '');
      setStartDate(formatDateToDDMMYYYY(initialEvent.startDate) || format(new Date(), 'dd/MM/yyyy'));
      setEndDate(formatDateToDDMMYYYY(initialEvent.endDate || initialEvent.startDate) || format(new Date(), 'dd/MM/yyyy'));
      setStartTime(initialEvent.startTime || '09:00');
      setEndTime(initialEvent.endTime || '10:00');
      setAllDay(Boolean(initialEvent.allDay));
      setReminderDay(initialEvent.reminderDay ?? '0');
      setReminderHour(initialEvent.reminderHour || '09:00');
      setRepeatType(initialEvent.repeatType || 'none');
      setRepeatInterval(initialEvent.repeatInterval ?? 1);
      setWeeklyRepeatCount(initialEvent.weeklyRepeatCount ?? 1);
      setMonthlyRepeatCount(initialEvent.monthlyRepeatCount ?? 1);
      setLocation(initialEvent.location || '');
      setLocationLat(null); setLocationLon(null);
      setEventColor(initialEvent.color || '#3d8ef0');
      setStartDateError(''); setEndDateError('');
      setWeeklyRepeatError(''); setMonthlyRepeatError('');
    } else if (!isEdit) {
      setTitle(''); setDescription('');
      setStartDate(selectedDate ? format(selectedDate, 'dd/MM/yyyy') : format(new Date(), 'dd/MM/yyyy'));
      setEndDate(selectedDate ? format(selectedDate, 'dd/MM/yyyy') : format(new Date(), 'dd/MM/yyyy'));
      setStartTime('09:00'); setEndTime('10:00');
      setAllDay(false); setReminderDay('0'); setReminderHour('09:00');
      setRepeatType('none'); setRepeatInterval(1);
      setWeeklyRepeatCount(1); setMonthlyRepeatCount(1);
      setLocation(''); setLocationLat(null); setLocationLon(null);
      setLocationSuggestions([]); setShowSuggestions(false); setLocationLoading(false);
      setEventColor('#3d8ef0');
      setStartDateError(''); setEndDateError('');
      setWeeklyRepeatError(''); setMonthlyRepeatError('');
      setShowReminderExpanded(false); setShowRepeatExpanded(false);
    }
  }, [isOpen, isEdit, initialEvent?.id, selectedDate]);

  const handleStartDateChange = (e) => {
    const v = e.target.value;
    setStartDate(v);
    setStartDateError(v && !isValidDate(v) ? t('createEventModal.invalidDateError') : '');
  };

  const handleEndDateChange = (e) => {
    const v = e.target.value;
    setEndDate(v);
    if (v && !isValidDate(v)) setEndDateError(t('createEventModal.invalidDateError'));
    else if (v && startDate && isValidDate(startDate) && formatDateFromDDMMYYYY(v) < formatDateFromDDMMYYYY(startDate)) setEndDateError(t('createEventModal.endBeforeStartError'));
    else setEndDateError('');
  };

  const handleLocationInput = useCallback((value) => {
    setLocation(value);
    setLocationLat(null);
    setLocationLon(null);

    // Marker'ı kaldır, Istanbul'a dön
    if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
    if (leafletMapRef.current) leafletMapRef.current.flyTo([ISTANBUL.lat, ISTANBUL.lon], ISTANBUL.zoom, { duration: 0.6 });

    if (!value.trim()) {
      setLocationSuggestions([]); setShowSuggestions(false); setLocationLoading(false);
      return;
    }

    clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    debounceRef.current = setTimeout(async () => {
      setLocationLoading(true);
      try {
        const controller = new AbortController();
        abortRef.current = controller;
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value)}&format=json&limit=6&addressdetails=1&accept-language=${i18n.language}`,
          { signal: controller.signal, headers: { 'Accept-Language': i18n.language } }
        );
        const data = await res.json();
        setLocationSuggestions(data);
        setShowSuggestions(data.length > 0);
      } catch (err) {
        if (err.name !== 'AbortError') { setLocationSuggestions([]); setShowSuggestions(false); }
      } finally {
        setLocationLoading(false);
      }
    }, 400);
  }, [i18n.language]);

  const handleSelectPlace = (result) => {
    setLocation(result.display_name);
    setLocationLat(result.lat);
    setLocationLon(result.lon);
    setLocationSuggestions([]);
    setShowSuggestions(false);
  };

  const handleClearLocation = () => {
    setLocation(''); setLocationLat(null); setLocationLon(null);
    setLocationSuggestions([]); setShowSuggestions(false); setLocationLoading(false);
    clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValidDate(startDate)) { setStartDateError(t('createEventModal.validStartDateError')); return; }
    if (!isValidDate(endDate)) { setEndDateError(t('createEventModal.validEndDateError')); return; }
    const sd = formatDateFromDDMMYYYY(startDate);
    const ed = formatDateFromDDMMYYYY(endDate);
    if (ed < sd) { setEndDateError(t('createEventModal.endBeforeStartError')); return; }
    if (!title.trim() || !calendar) return;
    if (repeatType === 'weekly') {
      const w = parseInt(weeklyRepeatCount, 10);
      if (isNaN(w) || w < 1 || w > 52) { setWeeklyRepeatError(t('createEventModal.weeklyRangeError')); return; }
    }
    if (repeatType === 'monthly') {
      const m = parseInt(monthlyRepeatCount, 10);
      if (isNaN(m) || m < 1 || m > 24) { setMonthlyRepeatError(t('createEventModal.monthlyRangeError')); return; }
    }
    const payload = {
      title: title.trim(), description: description.trim(),
      startDate: sd, endDate: ed,
      startTime: allDay ? null : startTime, endTime: allDay ? null : endTime,
      allDay, reminderDay, reminderHour,
      reminderTime: `${reminderDay}:${reminderHour}`,
      repeatType, repeatInterval,
      weeklyRepeatCount: repeatType === 'weekly' ? Math.max(1, parseInt(weeklyRepeatCount, 10) || 1) : 1,
      monthlyRepeatCount: repeatType === 'monthly' ? Math.max(1, parseInt(monthlyRepeatCount, 10) || 1) : 1,
      calendarId: calendar.id,
      location: location.trim() || null,
      locationLat: locationLat || null,
      locationLon: locationLon || null,
      color: eventColor,
    };
    if (isEdit && initialEvent?.id) onUpdate?.({ ...initialEvent, ...payload });
    else onCreate(payload);
    onClose();
  };

  if (!isOpen) return null;

  const repeatOptions = [
    { value: 'none', label: t('createEventModal.repeatOptions.none') },
    { value: 'weekly', label: t('createEventModal.repeatOptions.weekly') },
    { value: 'monthly', label: t('createEventModal.repeatOptions.monthly') },
    { value: 'yearly', label: t('createEventModal.repeatOptions.yearly') },
  ];
  const reminderDayOptions = [
    { value: '0', label: t('createEventModal.reminderOptions.sameDay') },
    { value: '1', label: t('createEventModal.reminderOptions.1day') },
    { value: '2', label: t('createEventModal.reminderOptions.2days') },
    { value: '3', label: t('createEventModal.reminderOptions.3days') },
    { value: '7', label: t('createEventModal.reminderOptions.1week') },
    { value: '14', label: t('createEventModal.reminderOptions.2weeks') },
  ];

  const inputCls = 'w-full px-3 py-2.5 bg-[#161f31] border border-white/10 rounded-xl text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/40 focus:border-[#3d8ef0]/60 transition-all';
  const timeCls = 'px-3 py-2 bg-[#161f31] border border-white/10 rounded-lg text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/40 focus:border-[#3d8ef0]/60 transition-all text-center min-w-0';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100000] flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 16 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 16 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[1000px] bg-[var(--color-bg-base)] rounded-2xl border border-white/[0.07] shadow-[0_24px_64px_rgba(0,0,0,0.65)] overflow-hidden"
          >
            <form onSubmit={handleSubmit}>

              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.07]">
                <div className="flex items-center gap-3">
                  <button type="button" onClick={onClose}
                    className="p-1.5 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-white/[0.05] transition-all">
                    <X className="w-4 h-4" />
                  </button>
                  <h2 className="text-[15px] font-semibold text-[var(--color-text-primary)]">
                    {isEdit ? t('createEventModal.editTitle') : t('createEventModal.createTitle')}
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={onClose}
                    className="px-4 py-1.5 text-sm text-[var(--color-text-secondary)] hover:bg-white/[0.05] rounded-lg transition-colors">
                    {t('createEventModal.cancel')}
                  </button>
                  <button type="submit"
                    className="px-5 py-1.5 text-sm bg-[#2563eb] hover:bg-[#3d8ef0] text-white font-semibold rounded-lg transition-colors">
                    {t('createEventModal.save')}
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="grid grid-cols-[1fr,380px] max-h-[calc(100vh-200px)] overflow-y-auto">

                {/* Sol kolon */}
                <div className="p-6 space-y-4">

                  {/* Başlık */}
                  <div>
                    <label className="block text-[11px] font-semibold text-[#8aaac8] mb-2 uppercase tracking-widest">{t('createEventModal.eventTitleLabel')}</label>
                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                      placeholder={t('createEventModal.eventTitlePlaceholder')} autoFocus required className={inputCls} />
                  </div>

                  {/* Açıklama */}
                  <div>
                    <label className="block text-[11px] font-semibold text-[#8aaac8] mb-2 uppercase tracking-widest">{t('createEventModal.descriptionLabel')}</label>
                    <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                      placeholder={t('createEventModal.descriptionPlaceholder')} rows={3} className={inputCls + ' resize-none'} />
                  </div>

                  {/* Konum */}
                  <div>
                    <label className="block text-[11px] font-semibold text-[#8aaac8] mb-2 uppercase tracking-widest">{t('createEventModal.locationLabel')}</label>

                    {/* Arama input */}
                    <div ref={inputWrapperRef} className="relative mb-2">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)] pointer-events-none" />
                      <input
                        ref={locationInputRef}
                        type="text"
                        value={location}
                        onChange={(e) => handleLocationInput(e.target.value)}
                        onFocus={() => locationSuggestions.length > 0 && setShowSuggestions(true)}
                        placeholder={t('createEventModal.locationPlaceholder')}
                        className={inputCls + ' pl-9 pr-9'}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {locationLoading ? (
                          <Loader2 className="w-3.5 h-3.5 text-[var(--color-text-muted)] animate-spin" />
                        ) : location ? (
                          <button type="button" onClick={handleClearLocation}
                            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        ) : null}
                      </div>

                      {/* Öneri dropdown — portal ile body'e render edilir (Leaflet z-index'ini bypass eder) */}
                      {showSuggestions && locationSuggestions.length > 0 && createPortal(
                        <AnimatePresence>
                          <motion.div
                            ref={suggestionsRef}
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.1 }}
                            style={{ position: 'fixed', top: suggestionPos.top, left: suggestionPos.left, width: suggestionPos.width, zIndex: 999999 }}
                            className="bg-[var(--color-bg-elevated)] border border-white/[0.08] rounded-xl overflow-hidden shadow-2xl"
                          >
                            {locationSuggestions.map((result, i) => {
                              const { main, secondary } = parseNominatimName(result);
                              return (
                                <button
                                  key={result.place_id || i}
                                  type="button"
                                  onClick={() => handleSelectPlace(result)}
                                  className="flex items-start gap-3 w-full px-3 py-2.5 text-left hover:bg-white/[0.05] transition-colors border-b border-white/[0.04] last:border-0"
                                >
                                  <div className="w-6 h-6 rounded-md bg-[#2563eb]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <MapPin className="w-3 h-3 text-[#3d8ef0]" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-xs font-medium text-[var(--color-text-primary)] truncate">{main}</p>
                                    {secondary && <p className="text-[11px] text-[var(--color-text-muted)] truncate mt-0.5">{secondary}</p>}
                                  </div>
                                </button>
                              );
                            })}
                          </motion.div>
                        </AnimatePresence>,
                        document.body
                      )}
                    </div>

                    {/* Harita — her zaman görünür */}
                    <div className="relative rounded-xl overflow-hidden border border-white/[0.08]" style={{ height: 220 }}>
                      <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

                      {/* Yol tarifi — sadece konum seçilince */}
                      {locationLat && locationLon && (
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${locationLat},${locationLon}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute bottom-2 left-2 z-[400] flex items-center gap-1.5 text-[11px] font-medium bg-[var(--color-bg-base)]/90 backdrop-blur-sm text-[var(--color-text-secondary)] px-2.5 py-1.5 rounded-lg border border-white/[0.12] hover:bg-[#2563eb] hover:text-white hover:border-[#3d8ef0] transition-all"
                        >
                          <Navigation className="w-3 h-3" />
                          {t('createEventModal.getDirections')}
                        </a>
                      )}
                    </div>
                  </div>

                </div>

                {/* Sağ kolon */}
                <div className="p-6 space-y-3 border-l border-white/[0.05]">

                  {/* Zamanlama */}
                  <div className="bg-[var(--color-bg-muted)] rounded-xl overflow-hidden border border-white/[0.06]">
                    <p className="px-4 pt-3 pb-2 text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-widest">{t('createEventModal.schedulingLabel')}</p>

                    <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/[0.06]">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-[#2563eb]/10 flex items-center justify-center">
                          <Clock className="w-3.5 h-3.5 text-[#3d8ef0]" />
                        </div>
                        <span className="text-sm font-medium text-[var(--color-text-primary)]">{t('createEventModal.allDayLabel')}</span>
                      </div>
                      <button type="button" onClick={() => setAllDay(!allDay)}
                        className={`relative inline-flex h-[22px] w-10 items-center rounded-full transition-colors duration-200 ${allDay ? 'bg-[#2563eb]' : 'bg-[#161f31] border border-white/10'}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${allDay ? 'translate-x-5' : 'translate-x-1'}`} />
                      </button>
                    </div>

                    <div className="px-4 py-2.5 border-t border-white/[0.06]">
                      <p className="text-[10px] text-[var(--color-text-muted)] font-medium mb-2">{t('createEventModal.startLabel')}</p>
                      <div className="flex gap-2">
                        <input type="text" value={startDate} onChange={handleStartDateChange} placeholder={t('createEventModal.datePlaceholder')} required
                          className={`flex-1 ${timeCls} ${startDateError ? 'border-red-500' : ''}`} />
                        {!allDay && <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={`w-[100px] shrink-0 ${timeCls}`} />}
                      </div>
                      {startDateError && <p className="text-red-400 text-xs mt-1">{startDateError}</p>}
                    </div>

                    <div className="px-4 py-2.5 border-t border-white/[0.06]">
                      <p className="text-[10px] text-[var(--color-text-muted)] font-medium mb-2">{t('createEventModal.endLabel')}</p>
                      <div className="flex gap-2">
                        <input type="text" value={endDate} onChange={handleEndDateChange} placeholder={t('createEventModal.datePlaceholder')} required
                          className={`flex-1 ${timeCls} ${endDateError ? 'border-red-500' : ''}`} />
                        {!allDay && <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className={`w-[100px] shrink-0 ${timeCls}`} />}
                      </div>
                      {endDateError && <p className="text-red-400 text-xs mt-1">{endDateError}</p>}
                    </div>
                  </div>

                  {/* Hatırlatma */}
                  <div className="bg-[var(--color-bg-muted)] rounded-xl overflow-hidden border border-white/[0.06]">
                    <button type="button" onClick={() => setShowReminderExpanded(!showReminderExpanded)}
                      className="flex items-center justify-between w-full px-4 py-3 hover:bg-white/[0.03] transition-colors">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-[#2563eb]/10 flex items-center justify-center">
                          <Bell className="w-3.5 h-3.5 text-[#3d8ef0]" />
                        </div>
                        <span className="text-sm font-medium text-[var(--color-text-primary)]">{t('createEventModal.reminderLabel')}</span>
                      </div>
                      <div className="flex items-center gap-1 text-[var(--color-text-muted)]">
                        <span className="text-xs">{reminderDayOptions.find(o => o.value === reminderDay)?.label || t('createEventModal.reminderOptions.sameDay')}</span>
                        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showReminderExpanded ? 'rotate-90' : ''}`} />
                      </div>
                    </button>
                    {showReminderExpanded && (
                      <div className="px-4 pb-3 border-t border-white/[0.06] pt-3 space-y-2">
                        <select value={reminderDay} onChange={(e) => setReminderDay(e.target.value)}
                          className="w-full px-3 py-2 bg-[#161f31] border border-white/10 rounded-lg text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/40 transition-all">
                          {reminderDayOptions.map(o => <option key={o.value} value={o.value} className="bg-[var(--color-bg-elevated)]">{o.label}</option>)}
                        </select>
                        <input type="time" value={reminderHour} onChange={(e) => setReminderHour(e.target.value)}
                          className="w-full px-3 py-2 bg-[#161f31] border border-white/10 rounded-lg text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/40 transition-all" />
                      </div>
                    )}
                  </div>

                  {/* Tekrar */}
                  <div className="bg-[var(--color-bg-muted)] rounded-xl overflow-hidden border border-white/[0.06]">
                    <button type="button" onClick={() => setShowRepeatExpanded(!showRepeatExpanded)}
                      className="flex items-center justify-between w-full px-4 py-3 hover:bg-white/[0.03] transition-colors">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-[#2563eb]/10 flex items-center justify-center">
                          <Repeat className="w-3.5 h-3.5 text-[#3d8ef0]" />
                        </div>
                        <span className="text-sm font-medium text-[var(--color-text-primary)]">{t('createEventModal.repeatLabel')}</span>
                      </div>
                      <div className="flex items-center gap-1 text-[var(--color-text-muted)]">
                        <span className="text-xs">{repeatOptions.find(o => o.value === repeatType)?.label || t('createEventModal.repeatOptions.none')}</span>
                        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showRepeatExpanded ? 'rotate-90' : ''}`} />
                      </div>
                    </button>
                    {showRepeatExpanded && (
                      <div className="px-4 pb-3 border-t border-white/[0.06] pt-3 space-y-2">
                        <div className="flex flex-wrap gap-1.5">
                          {repeatOptions.map((option) => {
                            const isLocked = option.value !== 'none' && !canUseRecurringEvents;
                            return (
                              <button key={option.value} type="button" disabled={isLocked}
                                onClick={() => {
                                  if (isLocked) { setShowUpgradeToast(true); setTimeout(() => setShowUpgradeToast(false), 4000); return; }
                                  setRepeatType(option.value);
                                  if (option.value !== 'weekly') { setWeeklyRepeatCount(1); setWeeklyRepeatError(''); }
                                  if (option.value !== 'monthly') { setMonthlyRepeatCount(1); setMonthlyRepeatError(''); }
                                }}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${isLocked ? 'bg-[#161f31] text-[var(--color-text-muted)] border border-white/10 cursor-not-allowed' : repeatType === option.value ? 'bg-[#2563eb]/20 text-[#7ab8f8] border border-[#3d8ef0]/30' : 'bg-[#161f31] text-[var(--color-text-secondary)] border border-white/10 hover:bg-[#24354f]'}`}>
                                {isLocked && <Lock className="w-3 h-3" />}
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                        <AnimatePresence>
                          {showUpgradeToast && (
                            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                              className="p-2.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg">
                              <div className="flex items-center gap-2">
                                <Crown className="w-4 h-4 text-white flex-shrink-0" />
                                <p className="text-white text-xs">{UPGRADE_MESSAGES.recurringEvents}</p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        {repeatType === 'weekly' && (
                          <div>
                            <input type="number" min={1} max={52} value={weeklyRepeatCount}
                              onChange={(e) => { setWeeklyRepeatCount(e.target.value); setWeeklyRepeatError(''); }}
                              placeholder={t('createEventModal.weeksPlaceholder')}
                              className="w-full px-3 py-2 bg-[#161f31] border border-white/10 rounded-lg text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/40" />
                            {weeklyRepeatError && <p className="text-red-400 text-xs mt-1">{weeklyRepeatError}</p>}
                          </div>
                        )}
                        {repeatType === 'monthly' && (
                          <div>
                            <input type="number" min={1} max={24} value={monthlyRepeatCount}
                              onChange={(e) => { setMonthlyRepeatCount(e.target.value); setMonthlyRepeatError(''); }}
                              placeholder={t('createEventModal.monthsPlaceholder')}
                              className="w-full px-3 py-2 bg-[#161f31] border border-white/10 rounded-lg text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/40" />
                            {monthlyRepeatError && <p className="text-red-400 text-xs mt-1">{monthlyRepeatError}</p>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Etkinlik Rengi */}
                  <div className="bg-[var(--color-bg-muted)] rounded-xl px-4 py-3 border border-white/[0.06]">
                    <p className="text-[10px] font-bold text-[var(--color-text-muted)] uppercase tracking-widest mb-3">{t('createEventModal.eventColorLabel')}</p>
                    <div className="flex gap-3">
                      {EVENT_COLORS.map((color) => (
                        <button key={color} type="button" onClick={() => setEventColor(color)}
                          className={`w-8 h-8 rounded-full transition-all duration-150 ${eventColor === color ? 'scale-110 ring-2 ring-white/50 ring-offset-2 ring-offset-[#161f31]' : 'hover:scale-105 opacity-70 hover:opacity-100'}`}
                          style={{ backgroundColor: color }} />
                      ))}
                    </div>
                  </div>

                </div>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreateEventModal;
